/**
 * The HOLDER's side of an IP arrangement — the "IP arrangement" section in the
 * C1 sharing drawer, and the header pill on S5 (the entity view).
 *
 * Built here, in components/sharing, because this lane owns the arrangement and
 * the AGG lane owns components/aggregator/** and pages/aggregator/**. The AGG
 * lane mounts these two exports; nothing in this file reaches into their files.
 *
 *   C1 drawer:  <IpArrangementSection arrangements={rows} loaded={...} />
 *               — drop it into SharingEditor between "What you can see" and
 *                 "A view into the portfolio?", which is where the figure puts it.
 *   S5 header:  <IpArrangementPill arrangements={rows} />
 *               — drop it in the entity title's pill row.
 *
 * WHAT THIS SIDE IS ALLOWED TO KNOW. The route behind these rows returns COUNTS
 * ONLY — "2 of 3 owners have acknowledged" — and never an owner's id, name or
 * email. That is not a display choice this component makes; the API does not
 * send them. So there is nothing here to leak, and nothing to be careful with.
 *
 * AND WHAT IT DOES NOT DO. An arrangement is a record, not a dial: it never
 * gives this organisation a project, a folder, or one extra byte of anything.
 * "What you can see" is set by the inventor on their own sharing screen, and
 * the sentence under this section says exactly that, because a Billing Owner
 * reading "assignment · acknowledged" would otherwise reasonably assume it did.
 */
/*
 * THREE CHANGES MADE BY THE AGG LANE (round 5 of the whole-screen gate, with the
 * coordinator's say-so, to be carried at landing): the default projects
 * collapse into ONE row naming them all, and every row is a key/value pair with
 * the value right-aligned. Both are the figure's own layout; the first also
 * stops a real inventor's twelve projects making the drawer taller than a
 * screen, which cost the sections below this one their place in the capture.
 * The third: this section carried its own px-4, so its top border ran 32px
 * wider than the borders of the sections either side of it (461px against
 * 429px). The padding is now the mounting drawer's, like theirs.
 */
import { useState } from 'react'
import { dayAndMonth } from './sharingModel'
import IpArrangementProposeDialog from './IpArrangementProposeDialog'
import {
  LIVE_STATUSES, PROPOSE_CONFIRMATION, acknowledgementSentence, currentArrangement, kindLabel,
} from './ipModel'
import type {
  IpArrangementHolderRow, IpArrangementProposal,
} from '../../types/ipArrangements'

export interface IpArrangementPillProps {
  arrangements: IpArrangementHolderRow[]
  /**
   * The reading organisation's own name, as the figure prints it
   * ("… on record: option — Meridian Ventures · 2 of 3 owners have
   * acknowledged"). Optional: when the mounting lane has no name to hand the
   * pill drops that clause rather than printing a UUID where a company belongs.
   */
  organisation?: string | null
}

/**
 * S5's header pill: "IP arrangement on record: option — 2 of 3 owners have
 * acknowledged". Renders nothing at all when there is no live record, because
 * "no arrangement" is the ordinary state of an entity and a pill announcing it
 * would be noise on every row.
 */
export function IpArrangementPill({ arrangements, organisation }: IpArrangementPillProps) {
  const current = currentArrangement(arrangements)
  if (!current) return null
  return (
    <span
      data-part="IpArrangementPill"
      title="An IP arrangement on one granted project. Every owner must acknowledge it, and it changes nothing about what this portfolio can see."
      className="inline-block whitespace-nowrap rounded-full border border-iflow-200 bg-iflow-50 px-2 py-0.5 text-[11px] font-semibold text-iflow-800"
    >
      IP arrangement on record: {current.kind} —{' '}
      {organisation ? `${organisation} · ` : ''}{acknowledgementSentence(current)}
    </span>
  )
}

export interface IpArrangementSectionProps {
  arrangements: IpArrangementHolderRow[]
  /** false only while a real read is in flight. */
  loaded: boolean
  /**
   * The granted projects that carry NO arrangement, as {id, name}. The figure
   * lists them as "Inventor retains (default)" beside the ones that do, so the
   * reader sees the whole picture rather than only the exceptions. Optional
   * because only the drawer knows which projects this portfolio was granted —
   * that list lives in the aggregator lane's own component, not here.
   */
  projectsWithout?: { id: string; name: string }[]
  /**
   * EVERY project this portfolio has been granted, for the propose picker. The
   * API refuses a project the inventor has not shared, so offering one would be
   * offering a button that cannot work. Falls back to projectsWithout when the
   * mounting lane has only that list to hand.
   */
  grantedProjects?: { id: string; name: string }[]
  /** A real read failure — never confused with "nothing on record". */
  error?: boolean
  /**
   * Puts a new arrangement on record. Optional: an Analyst is refused by the
   * API, so the aggregator lane passes it only for a Billing Owner or an Admin
   * and the link is simply absent for everyone else, rather than present and
   * rejected. Given this, the section opens the built-in picker itself, so the
   * mounting lane wires ONE component and gets the whole flow.
   */
  onSubmitProposal?: (proposal: IpArrangementProposal) => Promise<void>
}

/**
 * One key/value row: the project (or projects) on the left, what is on record
 * on the right, hard against the drawer's edge. The figure lays this section
 * out as a key/value list, and round 4 ran the value inline after the bold
 * project name, so nothing lined up down the section and the eye had to find
 * each value at a different x.
 *
 * FINAL POLISH — THE KEY CELL NO LONGER COLLAPSES TO ONE WORD PER LINE. The
 * union review caught this on C1x, on the live row for Meridian Process
 * Systems: the project title "CleanTech Grid Storage Platform" printed as
 * CleanTech / Grid / Storage / Platform, one word per line, in a cell measured
 * at 16.6px. The cause was the pair of utilities on the two cells: the key was
 * `min-w-0 flex-1` (basis 0, allowed to shrink to nothing) and the value was
 * `shrink-0` (never gives a pixel back), so the value took its whole 401px of
 * content and the key was left with what was over.
 *
 * The figure's own row is `display:flex; gap:10px` with BOTH cells at their
 * natural size (screen-C1-sharing-editor.html, `.kv`) — it never faces this
 * because its titles and its values are short enough to sit side by side. So
 * that is what this row now is, plus the one thing the figure does not need:
 *
 *   - the key grows but keeps its natural basis (`grow`, not `flex-1`), so it
 *     is measured at the width of its own text rather than at zero;
 *   - it carries a real floor (`min-w-[8rem]`) so it can never be squeezed to
 *     a word;
 *   - the row WRAPS. When the title and the value genuinely cannot share a
 *     line — 430px of drawer against a 221px title and a 401px value — the
 *     value drops to its own line, still right-aligned (`ml-auto`), and each
 *     half is printed whole. Measured in Chrome at the drawer's real 430px:
 *     the figure's own two rows still sit side by side exactly as it draws
 *     them, the long live row prints its title on ONE line and its value on
 *     one line, and nothing overflows at 320px either.
 */
function Pair({
  label, children, note, testId,
}: {
  label: string
  children: React.ReactNode
  note?: string
  testId?: string
}) {
  return (
    <div data-testid={testId} className="border-t border-slate-100 py-1.5 first:border-t-0">
      <div data-part="IpArrangementPairRow" className="flex flex-wrap items-baseline gap-2.5">
        <span
          data-part="IpArrangementPairKey"
          className="min-w-[8rem] grow text-[12px] font-semibold text-slate-700"
        >
          {label}
        </span>
        <span
          data-part="IpArrangementPairValue"
          className="ml-auto flex min-w-0 flex-wrap items-baseline justify-end gap-x-2 text-right"
        >
          {children}
        </span>
      </div>
      {note && <p className="mt-0.5 text-[11px] text-slate-500">{note}</p>}
    </div>
  )
}

export default function IpArrangementSection({
  arrangements, loaded, error = false, onSubmitProposal, projectsWithout = [],
  grantedProjects = [],
}: IpArrangementSectionProps) {
  const [proposing, setProposing] = useState(false)
  const live = arrangements.filter((a) => LIVE_STATUSES.has(a.status))
  const history = arrangements.filter((a) => !LIVE_STATUSES.has(a.status))

  return (
    <section data-part="IpArrangementSection" className="border-t border-slate-200 py-3">
      {/* The drawer's section headings are one style, and the figure fixes it:
          .sec-h is 11px/700, uppercase, letter-spacing .6px, iflow-700
          (screen-C1-sharing-editor.html). This heading sits beside the AGG
          lane's other drawer sections, so it follows theirs rather than
          inventing its own weight. */}
      <h3
        data-part="IpArrangementSectionHeading"
        className="mb-2 text-[11px] font-bold uppercase tracking-[0.6px] text-iflow-700"
      >
        IP arrangement
      </h3>

      {!loaded && !error && <p className="text-[12px] text-slate-400">Loading…</p>}

      {error && (
        <p className="text-[12px] leading-relaxed text-rose-600">
          Could not load this inventor&apos;s IP arrangements. This is not a
          statement that there are none.
        </p>
      )}

      {loaded && !error && live.length === 0 && projectsWithout.length === 0 && (
        <p className="text-[12px] leading-relaxed text-slate-600">
          Nothing on record. The inventor retains their IP, which is the default
          and needs no arrangement.
        </p>
      )}

      {loaded && !error && live.map((row) => (
        <Pair
          key={row.arrangement_id}
          label={row.project_name ?? 'A granted project'}
          note={row.reference ? `Reference: \u201C${row.reference}\u201D` : undefined}
        >
          <span className="text-[12px] text-slate-600">{kindLabel(row.kind)}</span>
          <span className="rounded-full border border-iflow-200 bg-iflow-50 px-2 py-px text-[11px] font-semibold text-iflow-800">
            {acknowledgementSentence(row)}
          </span>
          {row.proposed_at && (
            <span className="text-[11px] text-slate-500">
              proposed {dayAndMonth(row.proposed_at)}
            </span>
          )}
        </Pair>
      ))}

      {/* ONE row for every project the inventor simply retains, naming them all.
          The figure draws it this way and it is not only a matter of shape: a
          row per project put twelve lines in this section on a real inventor,
          which is what pushed the drawer past a screen and cost the sections
          below it their place. The default is one fact about many projects,
          so it reads as one line. */}
      {loaded && !error && projectsWithout.length > 0 && (
        <Pair
          label={projectsWithout.map((p) => p.name).join(' · ')}
          testId="ip-default-projects"
        >
          <span className="text-[12px] font-medium text-slate-500">Inventor retains (default)</span>
        </Pair>
      )}

      {loaded && !error && history.length > 0 && (
        <div className="mt-1.5 border-t border-slate-100 pt-1.5">
          {history.map((row) => (
            <p key={row.arrangement_id} className="text-[11px] text-slate-500">
              {row.project_name ?? 'A granted project'} · {kindLabel(row.kind)} ·{' '}
              {row.status}
              {row.proposed_at ? ` · proposed ${dayAndMonth(row.proposed_at)}` : ''}
            </p>
          ))}
        </div>
      )}

      {onSubmitProposal && !proposing && (
        <button
          type="button"
          onClick={() => setProposing(true)}
          className="mt-2 text-[12px] font-semibold text-iflow-600 hover:text-iflow-700"
        >
          Propose option / licence / assignment record →
        </button>
      )}

      {onSubmitProposal && proposing && (
        <IpArrangementProposeDialog
          grantedProjects={grantedProjects.length > 0 ? grantedProjects : projectsWithout}
          onSubmit={onSubmitProposal}
          onClose={() => setProposing(false)}
        />
      )}

      {/* The figure's fine print, verbatim — screen-C1-sharing-editor.html.
          The "(409)" and "and the button says so" clauses are the approved
          wording and are restored as such; the plain-English version of the
          same rule is what the picker actually SAYS when it refuses (see
          IpArrangementProposeDialog), which is the "button says so" half. */}
      <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
        The inventor confirms: “{PROPOSE_CONFIRMATION}” Every owner must
        acknowledge; any one may decline or dispute, ending it for all.
        <br />
        The <i>same</i> kind over a live record is refused (409) and the button
        says so; a <i>different</i> kind supersedes it. Superseded and withdrawn
        records stay as history, never deleted.
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
        None of this changes what you can see — that is the inventor&apos;s to
        give, project by project.
      </p>
    </section>
  )
}
